import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faArrowDown,
  faPlus,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { searchStock, createOrder } from "../../../api/user";
import { useNewTraderGuide } from "../../../context/NewTraderGuideContext";
import { useLeagueTrade } from "../../../context/LeagueTradeContext";
import { leagueTradeBlockReason } from "../../../utils/leagueAssets";
import { formatOrderPrice, formatTotal, formatCash, quantityLabel } from "../../../utils/orderFormat";
import useFreeCash from "../../../hooks/useFreeCash";
import useClosablePosition from "../../../hooks/useClosablePosition";
import ForexLotKeypad from "../ForexLotKeypad";
import PositionCloseActions from "../PositionCloseActions";
import ProcessingButton from "../../Skeleton/ProcessingButton";
import OrderTicketSkeleton from "../../Skeleton/OrderTicketSkeleton";

// Maps the order endpoint's existing refusal messages (lang/en/message.php
// STOCK.*, ORDER.ORDER_STATUS, LeagueAssets) to the client's launch copy.
const orderErrorText = (message) => {
  const m = String(message || "");
  if (m.startsWith("You have insufficient coins")) return "Not enough free cash for this order.";
  if (m.startsWith("You have insufficient holdings quantity")) return "Not enough available quantity for this order.";
  if (m.startsWith("You have insufficient short sell quantity")) return "Not enough available quantity for this order.";
  if (m === "The order has already been executed or closed.") return "This order is no longer open.";
  if (m === "This league has not started yet") return "Trading has not started in this league.";
  if (m === "This league has ended") return "This league has ended.";
  if (m === "Not eligible in this league") return "This asset is not eligible for this league.";
  return "Could not place the order. Try again.";
};

const getAssetType = (ticker) => {
  if (!ticker) return "stock";
  if (ticker.startsWith("C:")) return "forex";
  if (ticker.startsWith("X:")) return "crypto";
  return "stock";
};

const categoryFromSymbol = (sym) =>
  sym?.startsWith("C:") ? "Forex" : sym?.startsWith("X:") ? "Crypto" : "Stocks";

const positionTypeToTab = (pt) => {
  const n = Number(pt);
  if (n === 3) return "stopLoss";
  if (n === 1) return "market";
  return "limit";
};

const InlineBuySellPanel = ({ symbol, editOrder, onEditOrderDone }) => {
  // Null outside a league; inside one, every order it places belongs to that
  // league's book. See LeagueTradeContext.
  const league = useLeagueTrade();
  const { freeCash, refreshFreeCash } = useFreeCash({
    gameId: league?.gameId || null,
    enabled: !!symbol,
  });
  const navigate = useNavigate();
  const { guideActive, guideStep, advanceStep } = useNewTraderGuide();
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [tradeType, setTradeType] = useState(1); // 1 = Buy, 2 = Sell
  const [activeOrderTab, setActiveOrderTab] = useState("limit");
  const [quantity, setQuantity] = useState(0);
  // Forex Units keypad: lot size (units/lot) + lot count; quantity = size × count.
  const [forexLotSize, setForexLotSize] = useState(null);
  const [forexLotCount, setForexLotCount] = useState(1);
  const [limitPrice, setLimitPrice] = useState(0);
  const [lossPrice, setLossPrice] = useState(0);
  const [marketPrice, setMarketPrice] = useState(0);
  const [placing, setPlacing] = useState(false);
  const [showQuantityPopup, setShowQuantityPopup] = useState(false);
  const quantityPopupRef = useRef(null);
  const isEditMode = !!editOrder?.id;

  // Client rule 2: "Use the position from the current portfolio context, whether
  // that is the main account, a league, or a Cash Game." `league?.gameId` is
  // exactly the scope this ticket already places orders into, so the position
  // shown can never belong to a different book from the order being written.
  const { position, refreshPosition } = useClosablePosition({
    symbol,
    gameId: league?.gameId || null,
    enabled: !!symbol,
  });

  // Fills the ticket from one of the two shortcuts and gets out of the way.
  // Client: "Selecting one fills the quantity, selects the correct trade
  // direction, and closes the quantity menu."
  const applyPositionAction = (action) => {
    setQuantity(action.quantity);
    setTradeType(action.tradeType);
    setShowQuantityPopup(false);
  };

  // Close popup when clicking outside
  useEffect(() => {
    if (!showQuantityPopup) return;
    const handleClickOutside = (e) => {
      if (quantityPopupRef.current && !quantityPopupRef.current.contains(e.target)) {
        setShowQuantityPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showQuantityPopup]);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await searchStock({
          search: symbol,
          category: categoryFromSymbol(symbol),
        });
        if (cancelled) return;
        if (res?.data?.status === 200) {
          const data = res.data.data;
          setStockData(data);
          // Fresh symbol/order → clear any prior forex lot selection.
          setForexLotSize(null);
          setForexLotCount(1);
          const price = data?.min?.c || data?.prevDay?.c || 0;
          const assetType = getAssetType(data?.ticker);
          const formatted = formatOrderPrice(price, assetType);
          if (isEditMode) {
            const editPrice = Number(editOrder.order_price);
            const editFormatted = Number.isFinite(editPrice)
              ? formatOrderPrice(editPrice, assetType)
              : formatted;
            const tab = positionTypeToTab(editOrder.postion_type);
            setLimitPrice(tab === "stopLoss" ? formatted : editFormatted);
            setLossPrice(tab === "stopLoss" ? editFormatted : formatted);
            setMarketPrice(formatted);
            setQuantity(Number(editOrder.order_quantity) || 0);
            setActiveOrderTab(tab);
            // A short is order_type 3 (a sell to open). Treat both 2 (sell) and
            // 3 (short) as the Sell side so modifying a pending short/sell order
            // doesn't pre-fill as Buy and silently flip the side on update.
            setTradeType(
              Number(editOrder.order_type) === 2 ||
                Number(editOrder.order_type) === 3
                ? 2
                : 1
            );
          } else if (editOrder?.isHolding) {
            // Holdings buy/sell: price = current market, qty = held shares,
            // side = closing direction (long → Sell, short → Buy).
            setLimitPrice(formatted);
            setLossPrice(formatted);
            setMarketPrice(formatted);
            setQuantity(Number(editOrder.order_quantity) || 0);
            setActiveOrderTab(editOrder?.defaultStopLoss ? "stopLoss" : "limit");
            const ot = Number(editOrder.order_type);
            setTradeType(ot === 2 || ot === 3 ? 1 : 2);
          } else {
            setLimitPrice(formatted);
            setLossPrice(formatted);
            setMarketPrice(formatted);
            // Guide step 3: pre-fill Buy / Market / qty 1.
            if (guideActive && guideStep === 3) {
              setQuantity(1);
              setActiveOrderTab("market");
              setTradeType(1);
            } else {
              setQuantity(0);
              setActiveOrderTab("limit");
              setTradeType(1);
            }
          }
        }
      } catch (e) {
        if (!cancelled) setStockData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol, isEditMode, editOrder]);

  // Live price pushes keep the "market" price fresh
  useEffect(() => {
    const onLive = (e) => {
      if (e.detail && typeof e.detail.price === "number") {
        const assetType = getAssetType(stockData?.ticker);
        setMarketPrice(formatOrderPrice(e.detail.price, assetType));
      }
    };
    window.addEventListener("livePriceUpdate", onLive);
    return () => window.removeEventListener("livePriceUpdate", onLive);
  }, [stockData?.ticker]);

  const determineValue = () => {
    if (activeOrderTab === "market") {
      return (
        marketPrice || stockData?.min?.c || stockData?.prevDay?.c || 0
      );
    }
    return activeOrderTab === "stopLoss" ? lossPrice : limitPrice;
  };

  // Forex Units keypad handlers (Units = lot size × lot count).
  const forexSelectLot = (units) => {
    setForexLotSize(units);
    setForexLotCount(1);
    setQuantity(units);
  };
  const forexIncrement = () => {
    if (!forexLotSize) return;
    const n = forexLotCount + 1;
    setForexLotCount(n);
    setQuantity(forexLotSize * n);
  };
  const forexDecrement = () => {
    if (!forexLotSize) return;
    const n = Math.max(1, forexLotCount - 1);
    setForexLotCount(n);
    setQuantity(forexLotSize * n);
  };
  const forexClear = () => {
    setForexLotSize(null);
    setForexLotCount(1);
    setQuantity(0);
  };

  const handlePlaceOrder = async () => {
    if (!stockData?.ticker) {
      toast.error("Price data is unavailable. Try again.");
      return;
    }
    if (!quantity || Number(quantity) === 0) {
      toast.error("Please enter quantity");
      return;
    }

    // The league's own rules, before anything is submitted. Client, "League
    // Watchlist: launch behavior": "Right now, you can buy assets before the
    // league even starts, and you can buy assets that are not on the organizers'
    // list." Both refusals are enforced for real on the order endpoint (see
    // OrderController::assertLeagueAllowsTrade); this is the same answer given
    // immediately, in the same words, instead of after a round trip. Outside a
    // league `league` is null and nothing is checked.
    const leagueBlock = league?.game
      ? leagueTradeBlockReason(league.game, stockData.ticker)
      : null;
    if (leagueBlock) {
      toast.error(orderErrorText(leagueBlock));
      return;
    }

    // Client rule 3: "Recheck the position quantity when the order is
    // submitted." The ticket may have been sitting open while a stop fired or a
    // working order filled, so the number the shortcut wrote can be stale by the
    // time Place Order is pressed.
    //
    // Only for an order that CLOSES a position — a fresh buy or a new short is
    // not bounded by anything held, and running this on every order would add a
    // round trip to the common case for nothing.
    //
    // The server enforces this too (OrderController rejects with
    // INSAFICIANT_HOLDINGS / INSAFICIANT_SHORT_SELL). This is the same refusal
    // given before the order is sent rather than after, which is what stops a
    // user watching a spinner only to be told the size was never available.
    const closesLong = position?.direction === "long" && Number(tradeType) === 2;
    const closesShort = position?.direction === "short" && Number(tradeType) === 1;
    if (!isEditMode && (closesLong || closesShort)) {
      const fresh = await refreshPosition();
      const stillHeld = fresh?.held ?? 0;
      // Measured against the FULL net position, not the position minus resting
      // closing orders. Client's own case: "Long 500 with resting sell limit
      // 500. Manually sell 300. Result: hold 200, limit clamped to 200." A guard
      // that subtracted the resting 500 would have refused that 300.
      // Over-closing is prevented by the backend clamping resting orders inside
      // the same transaction as the fill, not by shrinking what may be typed.
      if (Number(quantity) > stillHeld) {
        toast.error(
          stillHeld > 0
            ? `You can close ${stillHeld} right now. Your position changed.`
            : "That position is already closed."
        );
        return;
      }
    }

    setPlacing(true);
    try {
      const data = {
        stock_symbol: stockData.ticker,
        asset_type: getAssetType(stockData.ticker),
        order_type: tradeType,
        order_quantity: quantity,
        order_price: determineValue(),
        postion_type:
          activeOrderTab === "stopLoss"
            ? 3
            : activeOrderTab === "limit"
            ? 2
            : 1,
      };
      if (isEditMode) {
        data.id = editOrder.id;
      }
      // Trading inside a league books the order against that league, never the
      // main account. Outside one, `league` is null and nothing is added — the
      // main Trade screen is unchanged.
      if (league?.gameId) {
        data.game_id = league.gameId;
      }
      const response = await createOrder(data);
      if (response?.data?.status === 200) {
        toast.success(isEditMode ? "Order updated." : "Order placed.");
        if (isEditMode) {
          onEditOrderDone && onEditOrderDone();
        } else {
          setQuantity(0);
        }
        refreshFreeCash();
        if (guideActive && guideStep === 3) {
          advanceStep(4);
          navigate("/portfolio");
        }
      } else {
        toast.error(orderErrorText(response?.data?.message));
      }
    } catch (e) {
      if (e?.response) {
        // Server answered and rejected it — a confirmed failure.
        toast.error(orderErrorText(e.response?.data?.message));
      } else {
        // No response: the order may or may not have been created. NO retry
        // affordance — resubmitting could duplicate the order.
        toast.error(
          "We could not confirm this order. Check your portfolio before trying again."
        );
      }
    } finally {
      setPlacing(false);
    }
  };

  const assetType = getAssetType(stockData?.ticker);

  // Client "Trade Screen Launch Polish", 4. Default order state: "When Shares
  // displays `0`, Total must display `0.00`. Do not show the cost of one share
  // before the user has selected one share."
  //
  // The zero branch used to be `formatTotal(determineValue())` — the price of a
  // SINGLE share — so a freshly opened ticket read Shares 0 / Total 187.42, a
  // cost for an order the user had not built yet.
  //
  // The literal "0.00" rather than formatTotal(0): formatTotal switches to four
  // decimals below 1, so it would render "0.0000". The client asked for `0.00`.
  //
  // This is symmetrical by construction — it is derived from `quantity` on every
  // render, so returning the quantity to zero (the keypad's C, or stepping down)
  // puts Total straight back to 0.00 with no reset handler of its own.
  const hasQuantity = Number(quantity) > 0;
  const totalPrice = hasQuantity
    ? formatTotal(determineValue() * Number(quantity))
    : "0.00";

  if (!symbol) return null;

  return (
    <div className="p-3 space-y-3 text-xs">
      {loading ? (
        <OrderTicketSkeleton compact />
      ) : (
        <>
          {/* Client Rev 3, 6: "Please add the same league portfolio context line
              that is now on the LEAGUE TRADE mobile order ticket to the desktop
              Trade order ticket: League portfolio · [dynamic league title]. Use
              Poppins, regular 400 weight, muted gray. Keep the same visual
              treatment on mobile and desktop. Desktop can use the additional
              available width before truncating the league title."

              Same node and the same classes as the mobile ticket
              (MobileChartToolbar) so the two cannot drift: text-xs, text-secondary
              (the muted grey token), Poppins from the app's default family,
              weight 400 by not setting one, left-aligned, one line.

              The ONE difference the client asked for is the truncation point.
              Mobile cuts the title at 17 characters because a phone's ticket is
              too narrow to trust the width; here the cut is left to CSS
              `truncate`, which ellipsises at whatever width this panel actually
              has — that IS "use the additional available width before
              truncating".

              Display-only, like the mobile one: a plain <p>, no handler, no role.
              It renders only inside a league; the main Trade screen's ticket is
              unchanged because `league` is null there. */}
          {league?.gameId && league?.title && (
            <p className="truncate whitespace-nowrap text-left text-xs text-secondary">
              League portfolio · {league.title}
            </p>
          )}

          {/* Trade Type */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTradeType(2)}
              className={`flex-1 h-9 rounded font-semibold text-xs flex items-center justify-center gap-1 transition-colors ${
                tradeType === 2
                  ? "bg-[#fe5b5e] text-white"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              <FontAwesomeIcon icon={faArrowDown} className="-rotate-45" />
              Sell
            </button>
            <button
              type="button"
              onClick={() => setTradeType(1)}
              className={`flex-1 h-9 rounded font-semibold text-xs flex items-center justify-center gap-1 transition-colors ${
                tradeType === 1
                  ? "bg-[#01cea7] text-white"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              <FontAwesomeIcon icon={faArrowUp} className="rotate-45" />
              Buy
            </button>
          </div>

          {/* Order tabs */}
          <div className="flex">
            {[
              { key: "limit", label: "Limit" },
              { key: "market", label: "Market" },
              { key: "stopLoss", label: "Stop" },
            ].map((t, idx, arr) => (
              <button
                type="button"
                key={t.key}
                onClick={() => setActiveOrderTab(t.key)}
                className={`flex-1 py-1.5 border border-[#004c50] text-[11px] font-semibold transition-colors ${
                  idx === 0 ? "rounded-l" : ""
                } ${idx === arr.length - 1 ? "rounded-r" : ""} ${
                  activeOrderTab === t.key
                    ? "bg-[#004c50] text-white"
                    : "bg-white text-[#004c50]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex items-baseline justify-between">
            <label className="block text-[10px] text-gray-600">Free Cash</label>
            <span className="text-xs font-normal text-gray-800">
              {freeCash === null ? "—" : formatCash(freeCash)}
            </span>
          </div>

          {/* Quantity / Price / Total */}
          <div className="grid grid-cols-3 gap-1.5">
            <div className="relative" ref={quantityPopupRef}>
              <label className="block text-[10px] text-gray-600 mb-1">
                {quantityLabel(assetType)}
              </label>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setShowQuantityPopup((v) => !v)}
                className="flex items-center justify-between px-2 py-1.5 bg-gray-100 rounded cursor-pointer border border-transparent hover:border-[#004c50]"
              >
                <span className="text-gray-800 text-xs font-medium flex-1 text-center">
                  {quantity}
                </span>
                <span className="text-gray-500 text-[9px]">+/−</span>
              </div>
              {showQuantityPopup && (
                <div
                  /* Client, "Desktop version only": "Can you move the qty box up
                     and maybe a touch to the right? Move it up enough so you can
                     see the free cash amount. The mobile version is good and not
                     being covered - no change."

                     `bottom-full` puts the popup's bottom edge on the top of this
                     cell — the "Coins"/"Shares"/"Units" label. The gap above that
                     is the ticket's own `space-y-3` (12px), and immediately above
                     THAT is the Free Cash row, 16px tall (its amount is text-xs,
                     so a 16px line box). mb-1 lifted the popup 4px, which left it
                     sitting over the Free Cash row.

                         12px  space-y-3 gap
                       + 16px  Free Cash row
                       +  4px  clearance
                       = 32px  = mb-8

                     so the popup's bottom now clears the top of the Free Cash row
                     rather than covering it.

                     The nudge right is the second half of the request: 115 -> 135
                     keeps the whole 150px popup inside the panel (the panel is
                     ~366px of content and the popup ends ~70px short of its right
                     edge), so nothing is pushed out of view.

                     MOBILE IS UNTOUCHED — it has its own order ticket in
                     components/chart/MobileChartToolbar.jsx and this file only
                     renders on desktop. */
                  className="absolute left-0 bottom-full mb-8 bg-[#1a1a2e] rounded-xl p-3 shadow-2xl z-50 w-[150px]"
                  style={{ transform: "translateX(135px)" }}
                >
                  {/* ABOVE the forex/non-forex split, not inside it. Client:
                      "the CLOSE POSITION feature only populates for stocks. It
                      does not populate for a crypto or forex Holdings
                      position." Forex was the branch — these chips were nested
                      in the arm that renders the ordinary keypad, so a forex
                      ticket rendered ForexLotKeypad instead and never reached
                      them. A position is a position whatever it is denominated
                      in, so the close row now sits above the split and both
                      keypads appear under it. */}
                  <PositionCloseActions
                    position={position}
                    onSelect={applyPositionAction}
                  />
                  {getAssetType(stockData?.ticker) === "forex" ? (
                    <ForexLotKeypad
                      lotSize={forexLotSize}
                      onSelectLot={forexSelectLot}
                      onIncrement={forexIncrement}
                      onDecrement={forexDecrement}
                      onClear={forexClear}
                    />
                  ) : (
                    <>
                  {/* Compact spacing (py-2 / gap-1.5) so this opens-upward popup
                      isn't tall enough to clip its top row against the panel's
                      scroll edge — matches the compact ForexLotKeypad. */}
                  <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        setQuantity((prev) => Math.max(0, Number(prev) - 1))
                      }
                      className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg"
                    >
                      <FontAwesomeIcon icon={faMinus} className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setQuantity((prev) => Number(prev) + 1)
                      }
                      className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 rounded-lg"
                    >
                      <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[10, 25, 100, 500, 1000].map((v) => (
                      <button
                        type="button"
                        key={v}
                        onClick={() =>
                          setQuantity((prev) => Number(prev) + v)
                        }
                        className="bg-[#2a2a3e] hover:bg-[#3a3a4e] text-white py-2 text-sm font-medium rounded-lg"
                      >
                        {v}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setQuantity(0)}
                      className="bg-[#2a2a3e] hover:bg-red-600 text-white py-2 text-sm font-medium rounded-lg"
                    >
                      C
                    </button>
                  </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">
                Price
              </label>
              <input
                type="text"
                value={determineValue()}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*\.?\d{0,6}$/.test(val)) {
                    activeOrderTab === "limit"
                      ? setLimitPrice(val)
                      : setLossPrice(val);
                  }
                }}
                disabled={activeOrderTab === "market"}
                className="w-full text-center bg-gray-100 rounded px-1 py-1.5 text-xs outline-none disabled:opacity-75"
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">
                Total
              </label>
              <input
                type="text"
                value={totalPrice}
                readOnly
                className="w-full text-center bg-gray-100 rounded px-1 py-1.5 text-xs outline-none"
              />
            </div>
          </div>

          {guideActive && guideStep === 3 && (() => {
            const persona = localStorage.getItem("qushi_onboarding_persona");
            const title = persona === "basics" ? "Your practice order is ready" : "Your order is ready";
            const subtitle = persona === "basics"
              ? "We filled this in for you. Tap Place Order to start building reps."
              : "We filled this in for you. Tap Place Order to buy with virtual money.";
            return (
              <div
                style={{
                  background: "#1B4D3E",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 14px",
                  textAlign: "center",
                  marginBottom: 8,
                  position: "relative",
                }}
              >
                <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
                  {title}
                </p>
                <p style={{ fontSize: 11, opacity: 0.9, lineHeight: 1.4 }}>
                  {subtitle}
                </p>
                <div
                  style={{
                    position: "absolute",
                    bottom: -7,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 0,
                    height: 0,
                    borderLeft: "7px solid transparent",
                    borderRight: "7px solid transparent",
                    borderTop: "7px solid #1B4D3E",
                  }}
                />
              </div>
            );
          })()}

          {/* Inline processing. `disabled:opacity-60` is gone deliberately —
              greying a busy button reads as permanently unavailable. */}
          {/* Same client point 4: "Place Order: disabled" while Shares is 0, and
              "Once the user selects at least one share ... enable Place Order.
              If the quantity returns to zero ... disable Place Order again."

              `unavailable` is ProcessingButton's existing "required input is not
              ready" state — a real `disabled` attribute plus the house
              opacity-60 treatment — as opposed to `processing`, which stays the
              in-flight state for the submit itself. The minimum order is still
              one whole share; handlePlaceOrder's own quantity guard is
              untouched, this just stops the button inviting a tap that could
              only ever produce an error toast. */}
          <ProcessingButton
            onClick={handlePlaceOrder}
            processing={placing}
            unavailable={!hasQuantity}
            unavailableClassName="opacity-60"
            processingLabel={isEditMode ? "Updating" : "Placing"}
            label={isEditMode ? "Update Order" : "Place Order"}
            className="w-full h-9 rounded-full bg-[#004c50] text-white text-xs font-semibold shadow hover:bg-[#003a3d] transition-colors"
          />
          {isEditMode && (
            <button
              type="button"
              onClick={() => onEditOrderDone && onEditOrderDone()}
              className="w-full h-8 rounded-full border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50"
            >
              Cancel Edit
            </button>
          )}
          {/* The simulated-trading disclaimer moved to a pinned footer in
              ChartRightWatchlist so it stays visible while this ticket scrolls. */}
        </>
      )}
    </div>
  );
};

export default InlineBuySellPanel;
